import { createContext, useContext, useState } from "react";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { Button, ButtonProps } from "@/v2-components/ui/button";

interface BaseProps {
  className?: string;
  children?: React.ReactNode;
}

interface CardContext {
  numStages: number;
  currentStage: number;
  next: () => void;
  prev: () => void;
  goTo: (stage: number) => void;
}

const CardContext = createContext<CardContext | null>(null);

export const useStepCardContext = () => {
  const context = useContext(CardContext);
  if (!context) {
    throw new Error(
      "StepCard compound components cannot be rendered outside the StepCard component",
    );
  }
  return context;
};

function Root({
  numStages = 0,
  currentStage = 0,
  children,
  className,
}: {
  numStages?: number;
  currentStage?: number;
} & BaseProps) {
  const [stage, setStage] = useState(currentStage);
  const next = () => setStage((s) => Math.min(s + 1, numStages - 1));
  const prev = () => setStage((s) => Math.max(s - 1, 0));
  const goTo = (stage: number) => setStage(stage);

  return (
    <CardContext.Provider
      value={{ numStages, currentStage: stage, next, prev, goTo }}
    >
      <div className={cn(className)}>{children}</div>
    </CardContext.Provider>
  );
}

const Stepper = ({ className }: { className?: string }) => {
  const { numStages, currentStage } = useStepCardContext();

  return (
    <div className={cn("grid auto-cols-fr grid-flow-col gap-x-3", className)}>
      {Array.from({ length: numStages }).map((_, index) => (
        <div
          key={index}
          className={cn(
            "h-1 w-full rounded-full transition-colors",
            index <= currentStage ? "bg-blue-60" : "bg-coolgray-20",
          )}
        />
      ))}
    </div>
  );
};

const Next = ({
  className,
  onClick,
  ...rest
}: React.PropsWithChildren<ButtonProps & { className?: string }>) => {
  const { next, currentStage, numStages } = useStepCardContext();
  const { t } = useTrans(["gnb"]);

  return (
    <Button
      variant={"blue-fill"}
      shape={"pill"}
      className={cn("min-h-0 px-5 py-3 text-sm", className)}
      onClick={(e) => {
        next();
        if (onClick) {
          onClick(e);
        }
      }}
      {...rest}
    >
      {t("Next")}
    </Button>
  );
};

const Prev = ({
  className,
  onClick,
  children,
  ...rest
}: React.PropsWithChildren<ButtonProps & { className?: string }>) => {
  const { prev, currentStage, numStages } = useStepCardContext();
  const { t } = useTrans(["index"]);

  return (
    <Button
      variant={"blue-outline"}
      shape={"pill"}
      className={cn("min-h-0 px-5 py-3 text-sm", className)}
      onClick={(e) => {
        prev();
        onClick?.(e);
      }}
      {...rest}
    >
      {children || t("Prev")}
    </Button>
  );
};

const StepCard = Object.assign(Root, {
  Next,
  Stepper,
  Prev,
});

export default StepCard;
